import { useState, useRef, useEffect } from "react";
import { Globe2, ChevronDown, Search, Loader2, LogOut } from "lucide-react";
import { LANGUAGES, useI18n } from "../i18n";
import { useAuth } from "../auth";

import NotificationsBell from "./NotificationsBell";

const NAV_LINKS = [
  { label: "Explore", id: "explore" },
  { label: "Countries", path: "/countries" },
  { label: "Communities", id: "ecosystem" },
  { label: "News", id: "verticals" },
  { label: "Culture", id: "stories" },
  { label: "About", id: "manifesto" },
];

export default function TopNav() {
  const { lang, setLang, tr, loading } = useI18n();
  const { user, signIn, signOut, busy } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (l) => {
    if (l.path) {
      window.location.href = l.path;
      return;
    }
    if (window.location.pathname !== "/") {
      window.location.href = `/#${l.id}`;
      return;
    }
    document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" });
  };
  const current = LANGUAGES.find((l) => l.code === lang);
  const filtered = LANGUAGES.filter((l) => (l.name + l.native).toLowerCase().includes(q.toLowerCase()));

  return (
    <header className="sticky top-0 z-50 bg-white/90 border-b border-[#E5EDF7]" style={{ backdropFilter: "blur(12px)" }}>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-4">
        <button onClick={() => { if (window.location.pathname !== "/") { window.location.href = "/"; } else window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2.5 shrink-0" data-testid="portal-logo">
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#0EA5E9] flex items-center justify-center">
            <Globe2 size={22} className="text-white" />
          </span>
          <span className="text-left leading-tight">
            <span className="block font-extrabold text-[#0F2557] text-lg tracking-tight">WorldPulse<span className="text-[#2563EB]">.pro</span></span>
            <span className="block text-[0.6rem] text-[#64748B] font-medium">{tr("Every Place. Every Community. Connected.")}</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-1 mx-auto">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`top-nav-${l.id || l.label.toLowerCase()}`}
              onClick={() => go(l)}
              className="px-3.5 py-2 text-sm font-semibold text-[#334155] hover:text-[#2563EB] rounded-lg hover:bg-[#EAF2FD] transition-colors duration-200"
            >
              {tr(l.label)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 ml-auto lg:ml-0">
          <div className="relative" ref={ref}>
            <button
              data-testid="language-switcher-button"
              onClick={() => setOpen(!open)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#334155] hover:text-[#2563EB] rounded-lg hover:bg-[#EAF2FD] transition-colors duration-200"
            >
              {loading ? <Loader2 size={16} className="animate-spin text-[#2563EB]" /> : <Globe2 size={16} className="text-[#2563EB]" />}
              <span className="hidden sm:inline">{current?.native || "Language"}</span>
              <ChevronDown size={14} />
            </button>
            {open && (
              <div data-testid="language-dropdown" className="absolute right-0 mt-2 w-72 bg-white border border-[#E5EDF7] rounded-2xl shadow-xl overflow-hidden">
                <div className="p-3 border-b border-[#EEF3FA] flex items-center gap-2">
                  <Search size={14} className="text-[#94A3B8]" />
                  <input
                    data-testid="language-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search 50+ languages…"
                    className="flex-1 text-sm focus:outline-none placeholder:text-[#94A3B8]"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto py-1">
                  {filtered.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`language-option-${l.code}`}
                      onClick={() => { setOpen(false); setQ(""); setLang(l.code); }}
                      className={`w-full text-left px-4 py-2.5 text-sm flex justify-between items-center hover:bg-[#EAF2FD] transition-colors ${lang === l.code ? "text-[#2563EB] font-bold bg-[#F3F8FE]" : "text-[#334155]"}`}
                    >
                      <span>{l.name}</span>
                      <span className="text-[#64748B]">{l.native}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {user ? (
            <div className="hidden md:flex items-center gap-2" data-testid="user-menu">
              <NotificationsBell />
              {user.picture ? (
                <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-8 h-8 rounded-full border border-[#BFD4F2]" data-testid="user-avatar" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-[#EAF2FD] flex items-center justify-center text-[#2563EB] font-bold text-xs">{(user.name || "U")[0]}</span>
              )}
              <span className="text-sm font-semibold text-[#0F2557] max-w-[9rem] truncate" data-testid="user-name">{user.name}</span>
              <button data-testid="sign-out-button" onClick={signOut} title={tr("Sign Out")} className="p-2 text-[#64748B] hover:text-[#DC2626] transition-colors">
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <button
              data-testid="sign-in-button"
              onClick={signIn}
              disabled={busy}
              className="hidden md:flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-[#334155] hover:text-[#2563EB] transition-colors disabled:opacity-60"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {tr("Sign In")}
            </button>
          )}
          <button
            data-testid="share-community-button"
            onClick={() => go({ id: "waitlist" })}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-bold px-4 lg:px-5 py-2.5 rounded-xl transition-all duration-200 active:scale-[0.97] shadow-md shadow-blue-200"
          >
            {tr("Share Your Community")}
          </button>
        </div>
      </div>
    </header>
  );
}
